import { RiskShareBarList, type RiskShareRow } from "./RiskShareBarList";
import { hazardKeyLabel, hazardKeyOf, hazardKeysFor, type AssetHazardResult } from "@/lib/results";
import type { HazardType } from "@shared/scoring/types";

interface HazardRiskBarChartProps {
  results: AssetHazardResult[];
  /** The hazards to render, already narrowed by the Hazard filter (see
   * orderedHazards in results-filters.ts). Rows are enumerated from this
   * list rather than from `results` so a hazard with no computed scores
   * still gets a visible empty bar instead of silently disappearing. */
  hazards: HazardType[];
}

/** One bar per summary line: Changing Precipitation gets a MAM and an OND
 * bar, never one combined bar. */
function hazardRows(results: AssetHazardResult[], hazards: HazardType[]): RiskShareRow[] {
  return hazardKeysFor(hazards, results).map((key) => {
    const computed = results.filter((r) => hazardKeyOf(r) === key && r.status === "computed" && r.risk !== undefined);
    const hi = computed.filter((r) => (r.risk ?? 0) >= 3).length;
    return { label: hazardKeyLabel(key, true), hi, tot: computed.length };
  });
}

export function HazardRiskBarChart({ results, hazards }: HazardRiskBarChartProps) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="border-b border-border px-4 py-3">
        <p className="text-sm font-semibold leading-5">Share of assets each hazard puts at high risk</p>
      </div>
      <div className="p-4">
        <RiskShareBarList
          rows={hazardRows(results, hazards)}
          emptyMessage="Not available yet — no hazards have computed risk scores"
        />
      </div>
    </div>
  );
}
